import { usePlayerByTagQuery } from '@/services/qbotApi';
import { useGetPlayerByCrTagQuery } from '@/services/qduelsApi';

/**
 * Identity bridge: resolve a Clash Royale tag → the owner's Logto `sub`.
 *
 * A CR opponent / public CR profile is known only by tag (no email). Two
 * independent sources can map a tag to a QLASH user:
 *
 *   1. QBot (PRIMARY) — `GET /api/v1/cr/players/by-tag/:tag`. Broadest
 *      coverage: QBot's SavedPlayer cache, read-through-seeded from the QLASH
 *      platform, so it knows any player it has ever seen.
 *   2. QDuels (FALLBACK) — `GET /v1/players/by-cr-tag/:tag`. Only players who
 *      explicitly linked their CR account in qduels.
 *
 * Resolution order is QBot first, QDuels only on a QBot miss (no data / 404 /
 * 400). The qduels query is `skip`-gated until QBot has settled and failed to
 * resolve, so we never fire a redundant fallback request when QBot succeeds.
 *
 * 404/400 from either source stay on the RTK Query error channel (we don't
 * surface them) — an unresolved tag simply yields `{ logtoUserId: undefined }`
 * and callers render no social buttons (no follow/friend reads fire).
 */
export function useResolveCrTagToSub(tag: string | undefined): {
  logtoUserId: string | undefined;
} {
  const trimmed = tag ?? '';

  // PRIMARY — QBot. Skipped without a tag.
  const { data: qbotData, isFetching: qbotFetching } = usePlayerByTagQuery(trimmed, {
    skip: !trimmed,
  });
  const qbotResolved = Boolean(qbotData?.logtoUserId);

  // FALLBACK — QDuels. Only after QBot has settled (not fetching) and missed.
  // Skipping while QBot is in flight avoids a wasted request when QBot will
  // resolve; once QBot fails to resolve, this fires.
  const { data: qduelsData } = useGetPlayerByCrTagQuery(trimmed, {
    skip: !trimmed || qbotFetching || qbotResolved,
  });

  return { logtoUserId: qbotData?.logtoUserId ?? qduelsData?.logtoUserId };
}
